/**
 * feature-config flag names owned by lifecycle
 *
 * please consider removing this file and adding it to an appropriate place in the
 * feature code (e.g. an entrypoint, or a package owned by the feature team), or
 * passing flag names directly as a string!
 *
 * by separating it out, we decouple the feature code from the feature config packages
 * and avoid the need to update this package when flags are added or removed.
 */

export const LIFECYCLE_FLAG_NAMES = {
  EXACT_SITE_USAGE_BILLING_PERIODS:
    'ff-lif-64-exact-site-usage-billing-periods',
  AISB_FEEDBACK: 'ff-lif-317-aisb-feedback',
  ADD_DATADOG_AISB_FLOWS: 'ff-lif-6-add-datadog-aisb-flows',
  AISB_V2: 'ff-lif-321-aisb-v2',
  // Sub-toggle of AISB_V2 covering only the branding surface: reference-URL
  // brand extraction and prompt-derived color/font/spacing interpretation.
  // Never evaluate this alone — see isAisbBrandingEnabled (server) and the
  // paired useFeatureFlag reads on the client.
  AISB_BRANDING_CONTROLS: 'ff-lif-451-toggle_aisb_branding_controls',
  PENDO_IPN_POC: 'ff-lif-348-pendo-ipn',
  MULTI_SKU_CHECKOUT_SITES: 'ff-lif-25-multi-sku-checkout-sites',
  MULTI_SKU_CHECKOUT_SITE_ADDONS: 'ff-lif-25-multi-sku-checkout-site-addons',
  FF_LIF_230_NEW_SURVEY_FLOW: 'ff-lif-230-new-survey-flow',
  USER_ACCOUNT_DELETION: 'ff-grow-5512-user-account-deletion',
  TRANSACTIONAL_EMAIL_TEMPLATE_UPDATE: 'ff-lif-96-template-update',
  DASHBOARD_PROGRESSIVE_LOADING: 'ff-lif-202-dashboard-progressive-loading',
  WAF_IN_MEMORY_MOCK: 'ff-lif-306-waf-in-memory-mock',
  LIF_222_WAF_CHANGE_HISTORY: 'ff-lif-222-waf-change-history',
  LIF_274_WAF_BOT_TOKENS: 'ff-lif-274-waf-bot-tokens',
  // Gates the admin Security tab's Approved bots section and its routes, read
  // with the admin's identity. waf-worker also reads it, by hostname, before
  // honoring an approved provider token, so it needs waf-worker in its target
  // apps. Sites opt in through Allow verified bots (bypassVerifiedBots).
  LIF_380_WAF_APPROVED_BOTS: 'ff-lif-380-waf-approved-bots',
  // Gates strict runtime validation of Cloudflare hostname metadata responses.
  // When off, preserves the pre-LIF-291 permissive behavior (malformed reads
  // silently normalize to empty metadata) so the read-modify-write path can
  // be reverted instantly if the new contract validation misfires.
  // Global kill switch only: the client is also reached from hostname-sync
  // paths without hydrated site identity, so per-site or percentage targeting
  // would not apply there.
  WAF_CLOUDFLARE_METADATA_CONTRACT_VALIDATION:
    'ff-lif-291-waf-metadata-contract-validation',
  // Runtime switch on the customer WAF policy projection into Workers KV.
  // Off, the KV store refuses rule-content writes before it picks a
  // transport, so a Cloudflare KV outage or a bad projection can be stopped
  // without a deploy. Reads, lists, tombstones, and deletes stay exempt so a
  // clear is never suppressed and the sweep can still run.
  // CLOUDFLARE_WAF_KV_NAMESPACE is the provisioning switch; this is the
  // operational one. Global kill switch: read with ambient identity, so the
  // only coherent values are 0 and 100 percent.
  CUSTOMER_WAF_KV_PROJECTION: 'ff-lif-378-customer-waf-kv-projection',
  FOLDERS_BOUNDED_SITE_FETCH: 'ff-lif-325-folders-bounded-site-fetch',
  AISB_ADD_CMS: 'ff-lif-320-aisb-add-cms',
  // Install-time switch for the logo polarity wiring: the Logo Invert
  // variables, the filter on logo images, and the surface overrides. A site
  // installed with this off carries none of those aliases, so the client emits
  // no polarity updates. Flipping it never changes a site that already exists.
  AISB_WIRE_THEMING_TO_LOGOS: 'ff-lif-246-aisb-wire-theming-to-logos',
  // Gates the Apps panel launcher notification dot, and with it the shared
  // `hasIndicator` prop's first consumer. The gate already existed from the
  // earlier attempt at this work, so the id keeps its original ticket number.
  APPS_PANEL_NOTIFICATION_DOT: 'ff-lif-316-apps-panel-notification-dot',
  DISABLE_MARKETPLACE_HIGH_TRAFFIC_EVENTS:
    'ff-lif-377-disable-marketplace-high-traffic-events',
  KNOCK_SITE_TRANSFER_REQUEST_EMAIL:
    'ff-lif-512-knock-site-transfer-request-email',
  // Sends the workspace removal emails through Knock instead of Iterable.
  // Evaluate with an explicit workspaceId: most removal callers run outside a request.
  KNOCK_WORKSPACE_REMOVAL_EMAILS: 'ff-lif-513-knock-workspace-removal-emails',
} as const;

export const LIFECYCLE_EXPERIMENT_NAMES = {
  PAYMENT_METHOD_EXPIRY: 'exp-growth-grow-2774-payment-method-expiry-retry',
  PLAN_CONFUSION_IA: 'exp-growth-grow-4704-plan-confusion',
  NEW_SITE_MODAL: 'exp-growth-grow-4799-new-site-modal',
  CUSTOM_DASHBOARD: 'exp-growth-grow-5016-sp-dashboard',
  ONBOARDING_PAYWALL: 'exp-growth-grow-5357-onboarding-paywall',
  SITES_SORT_PREFERENCE: 'exp-growth-grow-5360-sites-sort-preference',
} as const;
